import { useEffect, useState } from 'react';
import {
  Button,
  Dialog,
  DialogBody,
  DialogFooter,
  DialogHeader,
  Input,
  showToast,
} from '@repo/ui';

import { useRegisterClioCredential } from '../../queries/useClioCredentialsQuery';
import { useClioOAuthFlow } from './useClioOAuthFlow';

interface AddCredentialDialogProps {
  isOpen: boolean;
  onClose: () => void;
  onAuthorized: () => void;
  /** True if the tenant has no credentials yet — the first registration becomes primary. */
  isFirstCredential: boolean;
}

interface FormState {
  label: string;
  appId: string;
  clientId: string;
  clientSecret: string;
}

const EMPTY_FORM: FormState = { label: '', appId: '', clientId: '', clientSecret: '' };

export function AddCredentialDialog({
  isOpen,
  onClose,
  onAuthorized,
  isFirstCredential,
}: AddCredentialDialogProps) {
  const [form, setForm] = useState<FormState>(EMPTY_FORM);
  const [errors, setErrors] = useState<Partial<FormState>>({});
  const [registeredCredentialId, setRegisteredCredentialId] = useState<string | null>(null);
  const registerMutation = useRegisterClioCredential();
  const { startOAuth, isRunning } = useClioOAuthFlow({
    onSuccess: () => {
      setForm(EMPTY_FORM);
      setRegisteredCredentialId(null);
      onAuthorized();
      onClose();
    },
    onError: (message) => {
      if (registeredCredentialId) {
        // Register succeeded but the popup/callback didn't complete. The
        // credential is already in the bundle — close the dialog and refresh
        // the list so the user sees the new row with a Re-authorize button.
        showToast.error(
          `${message}. Click "Re-authorize" on the new row to finish.`,
        );
        setForm(EMPTY_FORM);
        setRegisteredCredentialId(null);
        onAuthorized();
        onClose();
        return;
      }
      showToast.error(message);
    },
  });

  useEffect(() => {
    if (!isOpen) {
      setForm(EMPTY_FORM);
      setErrors({});
    }
  }, [isOpen]);

  const isSubmitting = registerMutation.isPending || isRunning;

  const handleField = <K extends keyof FormState>(field: K, value: string) => {
    setForm((prev) => ({ ...prev, [field]: value }));
    if (errors[field]) {
      setErrors((prev) => ({ ...prev, [field]: undefined }));
    }
  };

  const validate = (): boolean => {
    const next: Partial<FormState> = {};
    if (!form.label.trim()) next.label = 'Label is required';
    if (!form.clientId.trim()) next.clientId = 'Client ID is required';
    if (!form.clientSecret.trim()) next.clientSecret = 'Client Secret is required';
    setErrors(next);
    return Object.keys(next).length === 0;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!validate() || isSubmitting) return;

    const redirectUri = `${window.location.origin}/clio-redirect`;
    try {
      const result = await registerMutation.mutateAsync({
        label: form.label.trim(),
        clientId: form.clientId.trim(),
        clientSecret: form.clientSecret,
        appId: form.appId.trim() || undefined,
        redirectUri,
      });
      setRegisteredCredentialId(result.credentialId);
      await startOAuth(result.oauthAuthorizeUrl, result.state);
    } catch {
      // Errors surfaced via showToast in mutation/hook
    }
  };

  return (
    <Dialog open={isOpen} onClose={isSubmitting ? () => undefined : onClose} size="md">
      <form onSubmit={handleSubmit} className="flex flex-col">
        <DialogHeader>
          <div>
            <h2 className="text-xl font-semibold text-xtnd-dark dark:text-xtnd-white">
              {isFirstCredential ? 'Connect Clio app' : 'Add another Clio app'}
            </h2>
            <p className="text-sm text-xtnd-dark-500 dark:text-xtnd-dark-200">
              {isFirstCredential
                ? 'Register your primary Clio app. You’ll authorize it on the next step.'
                : 'Register an additional Clio app. The primary app stays unchanged.'}
            </p>
          </div>
        </DialogHeader>

        <DialogBody className="space-y-4">
          <Input
            id="label"
            label="Label"
            value={form.label}
            placeholder="e.g. Primary firm app"
            helperText="Internal name to identify this app"
            onChange={(e) => handleField('label', e.target.value)}
            error={errors.label}
            disabled={isSubmitting}
            fullWidth
            required
          />
          <Input
            id="appId"
            label="App ID"
            value={form.appId}
            placeholder="(Optional) Clio Application ID"
            helperText="Optional. Used for display only."
            onChange={(e) => handleField('appId', e.target.value)}
            disabled={isSubmitting}
            fullWidth
          />
          <Input
            id="clientId"
            label="Client ID"
            value={form.clientId}
            placeholder="Enter Clio Client ID"
            onChange={(e) => handleField('clientId', e.target.value)}
            error={errors.clientId}
            disabled={isSubmitting}
            fullWidth
            required
          />
          <Input
            id="clientSecret"
            type="password"
            label="Client Secret"
            value={form.clientSecret}
            placeholder="Enter Clio Client Secret"
            onChange={(e) => handleField('clientSecret', e.target.value)}
            error={errors.clientSecret}
            disabled={isSubmitting}
            fullWidth
            required
          />
        </DialogBody>

        <DialogFooter>
          <Button type="button" variant="outline" onClick={onClose} disabled={isSubmitting}>
            Cancel
          </Button>
          <Button type="submit" variant="primary" loading={isSubmitting} disabled={isSubmitting}>
            {registerMutation.isPending
              ? 'Saving…'
              : isRunning
              ? 'Authorizing…'
              : 'Continue to Clio'}
          </Button>
        </DialogFooter>
      </form>
    </Dialog>
  );
}
